import { useQueryClient } from '@tanstack/react-query'
import { useContext, useState } from 'react'

import NAVAssignmentDropdown from 'components/brokers/needs_attention/NAVAssignmentDropdown'
import {
  filterByLabelAndExactValueCallback,
  setQueryDataCallback,
} from 'components/brokers/needs_attention/utils'
import BrokerContext from 'components/context/BrokerContext'
import TasksContext from 'components/context/TasksContext'
import { useGetBrokers } from 'components/hooks/needs_attention/useGetBrokers'
import ComboboxMultiSelectFilter, {
  type ComboboxFilterOption,
} from 'components/reusable/table/ComboboxMultiSelectFilter'
import { usePatchBrokersTasks } from 'utils/web_api'
import { type BrokersBroker } from 'utils/web_api.schemas'

type AssignTagsToNAVItemProps = {
  id: string
  assignedBrokers: BrokersBroker[]
}

const AssignBrokersToTask = ({
  id,
  assignedBrokers,
}: AssignTagsToNAVItemProps) => {
  const queryClient = useQueryClient()
  const { getTasksQueryKey } = useContext(TasksContext)
  const { currentBroker } = useContext(BrokerContext)
  const [query, setQuery] = useState('')

  const { data } = useGetBrokers()
  const brokers = data?.data as BrokersBroker[]

  const assignedBrokerIds = assignedBrokers.map((broker) => broker.id)

  const pinnedBrokerOptions: ComboboxFilterOption<string>[] = [
    ...(currentBroker
      ? [{ label: currentBroker?.broker_name, value: currentBroker?.id }]
      : []),
  ]

  const brokerOptions: ComboboxFilterOption<string>[] =
    brokers
      ?.map((broker) => ({
        label: broker.broker_name,
        value: broker.id,
      }))
      .filter((option) => option.value !== currentBroker?.id) || []

  const addAssigneesToTaskMutation = usePatchBrokersTasks()

  const handleSelectAssignee = (brokerId: string) => {
    addAssigneesToTaskMutation.mutate(
      {
        data: {
          task_ids: [id],
          // If the broker is already assigned, remove them, otherwise add them. We return the updated task
          // with the response so the assignedBrokerIds doesn't get out of sync.
          ...(assignedBrokerIds.includes(brokerId)
            ? { broker_ids_to_remove: [brokerId] }
            : { broker_ids_to_add: [brokerId] }),
        },
      },
      {
        onSuccess: (data) => {
          // Update cached data with new Tasks that include updated assignees.
          queryClient.setQueryData(getTasksQueryKey, setQueryDataCallback(data))
        },
      }
    )
  }

  const filteredPinnedBrokerOptions = pinnedBrokerOptions.filter(
    filterByLabelAndExactValueCallback(query)
  )
  const filteredBrokerOptions = brokerOptions.filter(
    filterByLabelAndExactValueCallback(query)
  )

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <ComboboxMultiSelectFilter<string>
        selectedValues={assignedBrokerIds}
        options={[
          { groupLabel: 'Pinned', options: filteredPinnedBrokerOptions },
          { groupLabel: 'Brokers', options: filteredBrokerOptions },
        ]}
        onSelectValue={handleSelectAssignee}
        placeholder='Search brokers to assign...'
        onValueChange={setQuery}
      >
        <NAVAssignmentDropdown
          noSelectionText='Assignee'
          pluralIdentifier='assignees'
          selectedValues={assignedBrokerIds}
          options={[...pinnedBrokerOptions, ...brokerOptions]}
          isPending={addAssigneesToTaskMutation.isPending}
          type='assignee'
        />
      </ComboboxMultiSelectFilter>
    </div>
  )
}

export default AssignBrokersToTask
